import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock } from "vue"

import { computed, nextTick, PropType, reactive, ref, watch } from 'vue'
import { useCmTableListRefresh } from '@/hooks/use-cm-table-list-refresh'
import { useI18n } from 'vue-i18n'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import CmTable from '@/components/cmTable/index.vue'
import SearchFilterBar from '@/components/searchFilterBar/index.vue'
import PreText from '@/views/dashboard/group/hardware/_components/pre-text/pre-text.vue'
import { HardwareService, ChangeRecordItem } from '../hardware.service'
import { FormType, ObjectFormItem, DateType } from '@/components/cmForm/types'
import { TableOpt } from '@/components/cmTable/types'
import { useParseTime } from '@/hooks'
import { formatTimeStamp } from '@/utils/time'
import {
  HARDWARE_DISK_CAPACITY_LABELS,
  HARDWARE_MEMORY_SPEC_LABELS,
  resolveHardwareDiskCapacityRangeMb,
  resolveHardwareMemorySpecRangeMb,
} from '../_utils/hardware-fixed-capacity-buckets'

type FilterKeys = 'dateRange' | 'memory_spec' | 'disk_capacity'

export default /*#__PURE__*/_defineComponent({
  __name: 'hardware-change-record-list',
  props: {
  deviceId: {
    type: String as PropType<string>,
    default: '',
  },
},
  setup(__props, { expose: __expose }) {

const CHANGE_TYPE_KEYS = ['', 'changeTypeCpu', 'changeTypeMemoryCapacity', 'changeTypeMemoryInfo', 'changeTypeHardDisk', 'changeTypeMotherboard', 'changeTypeGraphicsCard', 'changeTypeNetworkCard', 'changeTypeAudioCard', 'changeTypeDisplay'] as const

const props = __props

const { t, locale } = useI18n()

function changeTypeLabel(changeType?: number): string {
  if (changeType == null || !CHANGE_TYPE_KEYS[changeType]) return ''
  return t('terminalHardware.' + CHANGE_TYPE_KEYS[changeType])
}
const injector = useReflectiveInjector([HardwareService])
const hardwareService = injector.get(HardwareService)

const tableRef = ref<InstanceType<typeof CmTable>>()
const { refreshList } = useCmTableListRefresh(tableRef)

const queryParams = reactive<{
  dateRange?: [string, string]
  start_time?: number
  end_time?: number
  memory_spec?: string
  disk_capacity?: string
  page: number
  limit: number
}>({
  dateRange: undefined,
  start_time: undefined,
  end_time: undefined,
  memory_spec: undefined,
  disk_capacity: undefined,
  page: 1,
  limit: 20,
})

const filterFormColumns = computed<ObjectFormItem<typeof queryParams, FilterKeys>>(() => {
  void locale.value
  return {
    dateRange: {
      label: t('terminalHardware.changeRecordTimeRange'),
      type: FormType.Date,
      dateType: DateType.DateRange,
      outSearchFiledFlag: true,
      selectOpt: {
        clearable: true,
      },
      events: {
        change: (val: string[] | undefined) => {
          if (val?.length === 2) {
            const [start, end] = useParseTime(val as [string, string])
            queryParams.start_time = start
            queryParams.end_time = end
          } else {
            queryParams.start_time = undefined
            queryParams.end_time = undefined
          }
        },
      },
      dateOpt: {
        'value-format': 'YYYY-MM-DD',
        'start-placeholder': t('remoteAuth.startPlaceholder'),
        'end-placeholder': t('remoteAuth.endPlaceholder'),
      },
    },
    memory_spec: {
      colOpt: { style: 'flex: 0 0 160px' },
      label: t('terminalHardware.filterMemorySpec'),
      hiddenAllLabel: true,
      type: FormType.Select,
      dictData: HARDWARE_MEMORY_SPEC_LABELS.map(label => ({ label, value: label })),
      selectOpt: { clearable: true },
      outSearchFiledFlag: true,
      events: {
        change: () => void refreshList(),
      },
    },
    disk_capacity: {
      colOpt: { style: 'flex: 0 0 160px' },
      label: t('terminalHardware.filterDiskCapacity'),
      hiddenAllLabel: true,
      type: FormType.Select,
      dictData: HARDWARE_DISK_CAPACITY_LABELS.map(label => ({ label, value: label })),
      selectOpt: { clearable: true },
      outSearchFiledFlag: true,
      events: {
        change: () => void refreshList(),
      },
    },
  }
})

const pageOption = {
  pageNumName: 'page',
  pageSizeName: 'limit',
}

const tableOpt = computed<TableOpt>(() => {
  void locale.value
  return {
    columns: [
      {
        prop: 'time',
        label: t('terminalHardware.time'),
        width: 170,
        formatter: (row: ChangeRecordItem) => {
          const raw = row.time
          if (raw == null || raw === '') return '--'
          const sec = Number(raw)
          if (Number.isNaN(sec)) return '--'
          return formatTimeStamp(sec > 1e12 ? Math.floor(sec / 1000) : sec, 'YYYY-MM-DD HH:mm')
        },
      },
      { prop: 'change_item', label: t('terminalHardware.changeItem'), columnOpt: { minWidth: 200, showOverflowTooltip: true } },
      { prop: 'origin_setting', label: t('terminalHardware.originSetting'), columnOpt: { minWidth: 200 } },
      { prop: 'new_setting', label: t('terminalHardware.newSetting'), columnOpt: { minWidth: 200 } },
    ],
    bodyStyle: { padding: '10px 16px' },
    showActionBar: false,
    showSearch: false,
    showRightToolbar: false,
    height: '100%',
    path: 'list',
    totalPath: 'total',
    usePagination: false,
    disableScrollLoad: false,
    scrollLoadThreshold: 10,
    scrollLoadDelay: 100,
  }
})

function getListApi(params: any) {
  if (!props.deviceId) {
    return Promise.resolve({ list: [], total: 0 })
  }
  const memR = resolveHardwareMemorySpecRangeMb(queryParams.memory_spec)
  const diskR = resolveHardwareDiskCapacityRangeMb(queryParams.disk_capacity)

  return hardwareService.fetchChangeRecordData({
    device_id: props.deviceId,
    page: params.page,
    limit: params.limit,
    start_time: queryParams.start_time != null ? String(queryParams.start_time) : undefined,
    end_time: queryParams.end_time != null ? String(queryParams.end_time) : undefined,
    memory_capacity_min: memR?.minMb,
    memory_capacity_max: memR?.maxMb,
    whole_disk_total_min: diskR?.minMb,
    whole_disk_total_max: diskR?.maxMb,
  })
}

async function handleQuery(form: any) {
  if (form.dateRange?.length === 2) {
    const [s, e] = useParseTime(form.dateRange)
    form.start_time = s
    form.end_time = e
  }
  Object.assign(queryParams, form)
  await refreshList()
}

function handleReset() {
  queryParams.dateRange = undefined
  queryParams.start_time = undefined
  queryParams.end_time = undefined
  queryParams.memory_spec = undefined
  queryParams.disk_capacity = undefined
  queryParams.page = 1
  void refreshList()
}

/** 不在 immediate 再 refresh：有 deviceId 时与 CmTable 默认首载合并会双发；仅 deviceId 变化时补拉 */
watch(
  () => props.deviceId,
  (id) => {
    if (id) {
      queryParams.page = 1
      nextTick(() => void refreshList())
    }
  },
)

__expose({
  refreshData: () => void refreshList(),
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(CmTable, {
    ref_key: "tableRef",
    ref: tableRef,
    "table-opt": tableOpt.value,
    api: getListApi,
    "page-option": pageOption,
    modelValue: queryParams,
    "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((queryParams) = $event)),
    "has-permission": !!__props.deviceId
  }, {
    header: _withCtx(() => [
      _createVNode(SearchFilterBar, {
        modelValue: queryParams,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((queryParams) = $event)),
        "form-columns": filterFormColumns.value,
        "show-search": false,
        "popover-threshold": 0,
        onQuery: handleQuery,
        onReset: handleReset
      }, null, 8, ["modelValue", "form-columns"])
    ]),
    change_item: _withCtx(({ row }: { row: ChangeRecordItem }) => [
      _createTextVNode(_toDisplayString(row.change_item || changeTypeLabel(row.change_type) || '--'), 1)
    ]),
    origin_setting: _withCtx(({ row }: { row: ChangeRecordItem }) => [
      _createVNode(PreText, {
        content: row.origin_setting
      }, null, 8, ["content"])
    ]),
    new_setting: _withCtx(({ row }: { row: ChangeRecordItem }) => [
      _createVNode(PreText, {
        content: row.new_setting
      }, null, 8, ["content"])
    ]),
    _: 1
  }, 8, ["table-opt", "modelValue", "has-permission"]))
}
}

})